import { history_data } from "@screens/sampleData";
import { getHistoryDateFromFlutter } from "@services/timeHelpers";
import { StatusBlock } from "./Status";
import Icon1 from "@assets/icons/Icon";
import { UserAvatar } from "@components/ui/UserAvatar";
import { colors } from "sexy";
import { useState } from "react";
import { CustomDocument } from "@components/ui/documents/CustomDocument";
import { EmailSent, Org, Project, ProjectHistoryEvent, User } from "types";
import { EmailSentModal } from "@components/modals/EmailSentModal";
import { PhotoModal } from "@components/modals/PhotoModal";
import { DocumentModal } from "@components/modals/DocumentModal";
import { getPhotosByIdsFromProject } from "helpers/projectHelpers";
import { CustomImage } from "@components/ui/images/CustomImage";
import { useDataStore } from "@store/user_org";

const getDocumentsFromProject = (project?: Project) => {
  if (!project?.folders) return [];

  const documents: any[] = [];
  const extractDocs = (folders: any[]) => {
    folders.forEach((folder) => {
      if (folder.documents) {
        documents.push(...folder.documents);
      }
      if (folder.sub_folders) {
        extractDocs(folder.sub_folders);
      }
    });
  };

  extractDocs(project.folders);
  return documents;
};

export const VerticalTimeline = ({
  history_events = history_data,
  activeProject,
  sentEmails = [],
  userData,
  orgData,
  setActiveProject,
  setSentEmails,
  search = "",
  isGuest = false,
}: {
  history_events?: ProjectHistoryEvent[];
  activeProject?: Project;
  sentEmails?: EmailSent[];
  userData?: User;
  orgData?: Org;
  setActiveProject?: (project: Project) => void;
  setSentEmails?: (emails: EmailSent[]) => void;
  search?: string;
  isGuest?: boolean;
}) => {
  const [emailSentOpen, setEmailSentOpen] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [selectedDocument, setSelectedDocument] = useState<string | null>(null);
  const orgMembers = useDataStore((state) => state.orgMembers);

  // Resolve current user avatar from orgMembers instead of stale snapshot
  const resolveUser = (eventUser: ProjectHistoryEvent["user"]) => {
    const member = orgMembers.find((m) => m.id === eventUser.id);
    if (member) {
      return {
        photo_url: member.customization?.photo_url,
        avatar_seed: member.customization?.avatar_seed,
        display_name: member.infos?.display_name || eventUser.display_name,
      };
    }
    return { photo_url: eventUser.photo_url, avatar_seed: undefined, display_name: eventUser.display_name };
  };

  const normalizedSearch = search.trim().toLowerCase();
  const allDocuments = getDocumentsFromProject(activeProject);

  const seenIds = new Set<string>();
  const filteredEvents = (normalizedSearch
    ? history_events.filter((entry) => {
        const action = entry.action?.toLowerCase() || "";
        const userName = entry.user.display_name?.toLowerCase() || "";
        const controlPoint = entry.control_point?.name?.toLowerCase() || "";
        const emailSubject = entry.email?.subject?.toLowerCase() || "";
        const docNames = allDocuments
          .filter((doc) => entry.documents_id?.includes(doc.id))
          .map((doc) => doc.name?.toLowerCase() || "")
          .join(" ");
        return (
          action.includes(normalizedSearch) ||
          userName.includes(normalizedSearch) ||
          controlPoint.includes(normalizedSearch) ||
          emailSubject.includes(normalizedSearch) ||
          docNames.includes(normalizedSearch)
        );
      })
    : history_events
  ).filter((entry) => {
    // Deduplicate events by ID (guards against ArrayUnion duplicates in Firestore)
    if (entry.id) {
      if (seenIds.has(entry.id)) return false;
      seenIds.add(entry.id);
    }
    // For guests: hide photo events where every photo is hidden
    if (entry.photos_id && entry.photos_id.length > 0) {
      const visiblePhotos = getPhotosByIdsFromProject(activeProject, entry.photos_id, userData);
      if (visiblePhotos.length === 0) return false;
    }
    // For guests: hide document events where every document is internal (hidden)
    if (isGuest && entry.documents_id && entry.documents_id.length > 0) {
      const eventDocs = allDocuments.filter((doc) => entry.documents_id?.includes(doc.id));
      if (eventDocs.length > 0 && eventDocs.every((doc) => doc.hidden)) return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col max-h-full overflow-auto">
      {emailSentOpen && (
        <EmailSentModal
          email_sent_id={emailSentOpen}
          onClose={() => setEmailSentOpen(null)}
          sentEmails={sentEmails}
          activeProject={activeProject}
          orgData={orgData}
        />
      )}

      {selectedPhoto && (
        <PhotoModal
          onClose={() => setSelectedPhoto(null)}
          photo_id={selectedPhoto}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
        />
      )}

      {selectedDocument && activeProject && (
        <DocumentModal
          document_id={selectedDocument}
          activeProject={activeProject}
          setActiveProject={setActiveProject}
          onClose={() => setSelectedDocument(null)}
        />
      )}

      {filteredEvents.length === 0 && (
        <div className="flex flex-col items-center text-center py-8 gap-1">
          <div className="text-h2 font-medium text-neutral-700">
            {normalizedSearch ? "Aucun résultat" : "Aucun événement"}
          </div>
          <div className="text-h3 text-neutral-400">
            {normalizedSearch
              ? "Essayez avec d'autres mots-clés."
              : "Les événements du projet apparaîtront ici au fur et à mesure."}
          </div>
        </div>
      )}

      {filteredEvents
        .sort((a, b) => (a.date < b.date ? 1 : -1))
        .map((entry, index) => (
          <div key={index} className="flex flex-row">
            {/* Date column */}
            <div className="mr-2 text-h3 text-neutral-400 w-24 flex-shrink-0">
              {getHistoryDateFromFlutter(entry.date)}
            </div>

            {/* Timeline dot + line column */}
            <div className="flex flex-col items-center flex-shrink-0">
              <div className="w-2.5 h-2.5 rounded-full bg-neutral-400 mt-1"></div>
              {/* Line stretches for entire content height */}
              {index < filteredEvents.length - 1 && (
                <div className="w-0.5 bg-neutral-300 flex-1 my-2"></div>
              )}
            </div>

            {/* Content column */}
            <div className="flex flex-col gap-2 ml-6 flex-1 pb-6 max-w-[calc(100%-150px)]">
              <div className="flex flex-row gap-2 items-start">
                {(() => {
                  const resolved = resolveUser(entry.user);
                  return (
                    <UserAvatar
                      src={resolved.photo_url}
                      value={resolved.avatar_seed || resolved.display_name}
                      size={32}
                      className="w-8 h-8 rounded-full flex-shrink-0 border border-neutral-300 mt-0.5"
                    />
                  );
                })()}
                <div className="flex flex-col flex-1">
                  <div className="text-h2 font-semibold h-4.5">
                    {resolveUser(entry.user).display_name}
                  </div>
                  <div className="text-h3 text-neutral-400">{entry.action}</div>
                </div>
              </div>

              {entry.control_point?.new_status && (
                <div className="mt-4 mb-2 flex flex-col gap-1">
                  <StatusBlock
                    type={"success"}
                    label={entry.control_point.name}
                  />
                </div>
              )}

              {entry.documents_id && (
                <div className="flex flex-col mt-2 gap-0">
                  {getDocumentsFromProject(activeProject)
                    .filter((doc) => entry.documents_id?.includes(doc.id) && (!isGuest || !doc.hidden))
                    .map((doc, docIndex) => (
                      <CustomDocument
                        key={docIndex}
                        name={doc.name}
                        url={doc.file?.url || ""}
                        hidden={doc.hidden}
                        indentLeft={0}
                        fontSize={12}
                        onClick={() => setSelectedDocument(doc.id)}
                      />
                    ))}
                </div>
              )}

              {getPhotosByIdsFromProject(
                activeProject,
                entry.photos_id || [],
                userData,
              ).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2 max-w-full">
                  {getPhotosByIdsFromProject(
                    activeProject,
                    entry.photos_id || [],
                    userData,
                  ).map((photo, photoIndex) => (
                    <CustomImage
                      key={photoIndex}
                      source={{ uri: photo.file.url }}
                      alt={`Photo ${photoIndex + 1}`}
                      className="rounded-xl w-100 h-24 cursor-pointer"
                      hidden={photo.hidden}
                      onClick={() => setSelectedPhoto(photo.id)}
                    />
                  ))}
                </div>
              )}

              {entry.email && (
                <div
                  className="mt-3 p-2.5 w-fit bg-neutral-100 rounded-lg border-2 border-neutral-200 cursor-pointer hover:bg-neutral-150 transition-colors"
                  onClick={() => {
                    setEmailSentOpen(entry.email?.email_sent_id || null);
                  }}
                >
                  <div className="text-h3 text-neutral-400">
                    {entry.email.subject}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
    </div>
  );
};
